<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { LogOut, Search } from 'lucide-vue-next';
import { useUserStore } from '@/stores/user.store';
import { Button } from '@/components/ui/button';
import Icon from '@/components/ui/Icon.vue';
import { sectionForPath } from '@/config/app';
import { usePageSearch } from '@/composables/usePageSearch';

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const { user, avatarUrl } = storeToRefs(userStore);

// Same role gate the command palette applies, shared so the two cannot diverge.
const { isOpen: isSearchOpen, isPermitted } = usePageSearch();

// A single sidebar component drives every section (depot, mantle, 125data, and
// the workspace default); APP_SECTIONS is what maps the route to which one.
const section = computed(() => {
	return sectionForPath(route.path);
});

const sectionMenu = computed(() => {
	return section.value.menu;
});

// Short, monospace environment label shown under the brand mark — mirrors the
// depot sidebar's "Depot · production" treatment for visual parity. The
// workspace fallback is not an environment, so it shows its label bare.
const brandEnv = computed(() => {
	return section.value.prefix === '/'
		? section.value.label
		: `${section.value.label} · production`;
});

interface NavChild {
	title: string;
	path: string;
}
interface NavItem {
	title: string;
	icon: string;
	path: string;
	children?: NavChild[];
}
interface NavGroup {
	label: string;
	items: NavItem[];
}

const groups = computed<NavGroup[]>(() => {
	return Object.values(sectionMenu.value).map((group) => {
		return {
			label: group.name,
			items: group.routes.filter(isPermitted).map((r) => {
				const children = (r.children ?? []).filter(isPermitted).map((c) => {
					return { title: c.title, path: c.path };
				});

				return {
					title: r.title,
					icon: r.icon,
					path: r.path,
					...(children.length ? { children } : {}),
				};
			}),
		};
	});
});

const userName = computed(() => {
	return user?.value?.name || 'User';
});
const userEmail = computed(() => {
	return user?.value?.username || '';
});
const userInitials = computed(() => {
	const n = userName.value || '';
	const parts = n.split(/\s+/).filter(Boolean);
	if (parts.length === 0) {
		return 'U';
	}

	return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || 'U';
});

// Highlight the most specific matching item only, so a section-root link like
// "/depot" doesn't stay active on every "/depot/*" sub-page (the deeper item
// wins via the longest matching prefix).
const isWithin = (path: string) => {
	return route.path === path || route.path.startsWith(path + '/');
};

const activePath = computed(() => {
	const paths = groups.value.flatMap((g) => {
		return g.items.flatMap((i) => {
			return [
				i.path,
				...(i.children ?? []).map((c) => {
					return c.path;
				}),
			];
		});
	});
	const matches = paths.filter(isWithin);
	return matches.reduce((best, p) => {
		return p.length > best.length ? p : best;
	}, '');
});

const isOn = (path: string) => {
	return path === activePath.value;
};

const handleNav = (path: string) => {
	return router.push(path);
};

const handleLogout = () => {
	return userStore.logout();
};
</script>

<template>
	<aside class="ds-sidebar">
		<div class="ds-sidebar-brand">
			<RouterLink to="/" class="logomark cursor-pointer no-underline">
				<span class="mark"></span>
				<span>125 Insights</span>
			</RouterLink>
			<span class="brand-env">{{ brandEnv }}</span>
		</div>

		<!-- A button, not a text field: typing here never searched anything, the
		     palette does. -->
		<button
			type="button"
			class="ds-sidebar-search"
			data-tour="shell-search"
			title="Search pages"
			@click="isSearchOpen = true"
		>
			<Search :size="13" />
			<span class="ds-sidebar-search__label">Search…</span>
			<span
				class="rounded-[4px] border border-[color:var(--depot-border)] bg-[color:var(--bg)] px-[5px] py-px font-mono text-[10px] text-[color:var(--fg-faint)]"
				>⌘K</span
			>
		</button>

		<nav class="ds-sidebar-nav" data-tour="shell-nav">
			<div v-for="group in groups" :key="group.label" class="ds-nav-group">
				<div class="group-label">{{ group.label }}</div>
				<ul>
					<li v-for="item in group.items" :key="item.path">
						<a
							:class="{ on: isOn(item.path) }"
							:href="item.path"
							@click.prevent="handleNav(item.path)"
						>
							<span class="ic">
								<Icon :name="item.icon" :size="14" :stroke-width="1.6" />
							</span>
							<span>{{ item.title }}</span>
						</a>
						<ul v-if="item.children && isWithin(item.path)" class="ds-nav-sub">
							<li v-for="child in item.children" :key="child.path">
								<a
									:class="{ on: isOn(child.path) }"
									:href="child.path"
									@click.prevent="handleNav(child.path)"
								>
									<span>{{ child.title }}</span>
								</a>
							</li>
						</ul>
					</li>
				</ul>
			</div>
		</nav>

		<div class="ds-sidebar-foot">
			<button class="user-tile" type="button" @click="router.push('/user/profile')">
				<div class="avatar">
					<img
						v-if="avatarUrl"
						:src="avatarUrl"
						:alt="userName"
						class="h-full w-full rounded-full object-cover"
					/>
					<template v-else>{{ userInitials }}</template>
				</div>
				<div class="who">
					<span class="name">{{ userName }}</span>
					<span class="mail">{{ userEmail }}</span>
				</div>
			</button>
			<Button
				variant="ghost"
				size="sm"
				class="logout-btn mt-1 w-full justify-start gap-2 text-[color:var(--fg-muted)]"
				title="Log out"
				type="button"
				@click.stop="handleLogout"
			>
				<LogOut :size="14" :stroke-width="1.6" />
				<span class="logout-label">Log Out</span>
			</Button>
		</div>
	</aside>
</template>

<style scoped>
.ds-nav-sub {
	margin-left: 22px;
	border-left: 1px solid var(--depot-border);
}
.ds-nav-sub a {
	padding-left: 12px;
	font-size: 12px;
}

/* Match the depot sidebar's collapsed (icon-only) behaviour: drop the logout
   label and centre the button when the rail narrows. Sub page links go with
   them: the collapsed rail hides every label, and a child has no icon to fall
   back on, so it would render as an unlabelled blank row. */
@media (max-width: 980px) {
	.logout-label {
		display: none;
	}
	.logout-btn {
		justify-content: center;
	}
	.ds-nav-sub {
		display: none;
	}
}
</style>
